import { useState, useRef, useEffect } from 'react';

export default function SearchableSelect({
    options = [],
    value,
    onChange,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    labelKey = 'name',
    valueKey = 'id',
    className = '',
    error = false,
    disabled = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    const selectedOption = options.find(opt => String(opt[valueKey]) === String(value));

    const filteredOptions = options.filter(opt =>
        opt[labelKey].toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option ? option[valueKey] : '');
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div ref={containerRef} className={`searchable-select ${className}`}>
            <div
                className={`form-select d-flex align-items-center justify-content-between ${error ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
                <span className={selectedOption ? '' : 'text-muted'}>
                    {selectedOption ? selectedOption[labelKey] : placeholder}
                </span>
                <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-2`}></i>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="p-2 border-bottom">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </span>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control border-start-0"
                                placeholder={searchPlaceholder}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            {search && (
                                <button
                                    className="btn btn-outline-secondary border-start-0"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSearch('');
                                    }}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="searchable-select-options">
                        {value && (
                            <div
                                className="searchable-select-option text-muted"
                                onClick={() => handleSelect(null)}
                            >
                                <i className="bi bi-x-circle me-2"></i>
                                Clear selection
                            </div>
                        )}

                        {filteredOptions.length === 0 ? (
                            <div className="searchable-select-option text-muted text-center">
                                No results found
                            </div>
                        ) : (
                            filteredOptions.slice(0, 100).map((option) => (
                                <div
                                    key={option[valueKey]}
                                    className={`searchable-select-option ${String(option[valueKey]) === String(value) ? 'active' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option[labelKey]}
                                    {String(option[valueKey]) === String(value) && (
                                        <i className="bi bi-check2 ms-auto"></i>
                                    )}
                                </div>
                            ))
                        )}

                        {filteredOptions.length > 100 && (
                            <div className="searchable-select-option text-muted text-center small">
                                Showing first 100 results. Type to search more...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
